import gql from 'graphql-tag';

import flattenConnection from '@watershed/shared-universal/utils/flattenConnection';

import { GQGetAllAssignableUsersWithPermissionBatchQuery } from '@watershed/app-dashboard/generated/graphql-operations';

// More efficient batched query. Recommended for new callers.
//
// IMPORTANT: If the input list of required permissions is empty, `usersWithPermissions` will return an empty list.
// If you want to transform this result into the same format as the older non-batched query, see the helper
// function below.
gql`
  query GetAllAssignableUsersWithPermissionBatch(
    $permissions: [HasPermissionInput!]!
    $withAllPermissions: [HasPermissionInput!]
    $orgId: ID!
  ) @withOwner(owner: ApplicationPlatform) {
    organization {
      id
      usersWithPermissions: users(
        filters: {
          withSomePermissions: $permissions
          withAllPermissions: $withAllPermissions
          includeWatershedEmployees: true
        }
      ) {
        edges {
          node {
            id
          }
        }
      }
      users {
        edges {
          node {
            ...AssignableUserFields
            orgAccessId(orgId: $orgId)
          }
        }
      }
    }
  }
`;

// Helper function for transforming results of the batched query to match the results
// of the legacy non-batched query.
// Adds a `hasPermissions` boolean property to each user entry. This will be true if no
// permissions are required.
export function addHasPermissionsFieldToBatchAssigneeQueryResult({
  data,
  noPermissionsRequired,
}: {
  data: GQGetAllAssignableUsersWithPermissionBatchQuery | undefined;
  noPermissionsRequired: boolean;
}) {
  const usersWhoHavePermission: Array<string> = flattenConnection(
    data?.organization?.usersWithPermissions
  ).map((it) => it.id);
  const userData = flattenConnection(data?.organization?.users).map((user) => ({
    ...user,
    hasPermissions:
      usersWhoHavePermission.includes(user.id) || noPermissionsRequired,
  }));
  return userData;
}

// Legacy non-batched query included for back-compatibility. Safe to use in contexts where
// you won't be querying many times in rapid succession.
//
// If the permissions input list is empty, hasPermissions will be true for all users.
//
// WARNING: The users hasPermission selector has an N+1 query perf problem that is difficult to fix
// (not FKLoader-compatible) and can overload the db if too many of these queries are triggered quickly!
gql`
  query GetAllAssignableUsersWithPermission(
    $permissions: [HasPermissionInput!]!
    $orgId: ID!
  ) @withOwner(owner: ApplicationPlatform) {
    organization {
      id
      users {
        edges {
          node {
            hasPermissions(permissions: $permissions, orgId: $orgId)
            hasAnyPermissions(permissions: $permissions, orgId: $orgId)
            ...AssignableUserFields
            orgAccessId(orgId: $orgId)
          }
        }
      }
    }
  }
`;
